// Generate random amount between min and max
const randomAmount = (min, max) => 
  Number((Math.random() * (max - min) + min).toFixed(2));

// Generate random date within last 30 days
const randomDate = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

const mockTransactionTypes = [
  { type: 'debit', description: 'Amazon.com Purchase' },
  { type: 'debit', description: 'Target Store' },
  { type: 'debit', description: 'Whole Foods Market' },
  { type: 'debit', description: 'Shell Gas Station' },
  { type: 'debit', description: 'Netflix Subscription' },
  { type: 'debit', description: 'Uber Ride' },
  { type: 'debit', description: 'Starbucks' },
  { type: 'credit', description: 'Direct Deposit - Employer' },
  { type: 'credit', description: 'ACH Transfer Credit' },
  { type: 'credit', description: 'Refund - Amazon' },
  { type: 'debit', description: 'Mobile Phone Bill' },
  { type: 'debit', description: 'Electric Bill Payment' },
  { type: 'debit', description: 'Internet / Cable Payment' },
  { type: 'debit', description: 'Restaurant Payment' },
  { type: 'credit', description: 'Wire / ACH Transfer' }
];

export const generateMockTransactions = (count = 20) => {
  return Array.from({ length: count }, () => {
    const transaction = mockTransactionTypes[Math.floor(Math.random() * mockTransactionTypes.length)];
    return {
      type: transaction.type,
      description: transaction.description,
      amount: transaction.type === 'credit' 
        ? randomAmount(100, 3000)
        : randomAmount(5, 200),
      date: randomDate(),
      status: 'completed',
      category: transaction.type === 'credit' ? 'income' : 'expense'
    };
  }).sort((a, b) => b.date - a.date);
};

export const initialAccounts = [
  {
    type: 'Checking',
    accountNumber: '1234567890',
    balance: 2500.0,
    routingNumber: '021000021'
  },
  {
    type: 'Savings',
    accountNumber: '0987654321',
    balance: 10000.0,
    routingNumber: '021000021'
  }
];

// Returns mock transactions with dates relative to "now" so they always look recent
function daysAgo(days, hour = 10, minute = 0) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hour, minute, 0, 0);
  return d;
}

export function getMockTransactions() {
  const now = new Date();
  return [
    {
      id: 'mock-1',
      description: 'Direct Deposit - Employer Payroll',
      amount: 2850.00,
      type: 'credit',
      date: daysAgo(2, 10, 0),
      category: 'Income',
      status: 'Completed',
      isMock: true
    },
    {
      id: 'mock-2',
      description: 'Monthly Rent Payment',
      amount: 1500.00,
      type: 'debit',
      date: daysAgo(5, 9, 30),
      category: 'Housing',
      status: 'Completed',
      isMock: true
    },
    {
      id: 'mock-3',
      description: 'Grocery Purchase - Kroger',
      amount: 156.78,
      type: 'debit',
      date: daysAgo(3, 15, 45),
      category: 'Shopping',
      status: 'Completed',
      isMock: true
    },
    {
      id: 'mock-4',
      description: 'Car Insurance - Quarterly Payment',
      amount: 385.00,
      type: 'debit',
      date: daysAgo(14, 11, 20),
      category: 'Insurance',
      status: 'Completed',
      isMock: true
    },
    {
      id: 'mock-5',
      description: 'ATM Withdrawal',
      amount: 100.00,
      type: 'debit',
      date: daysAgo(21, 14, 15),
      category: 'Withdrawal',
      status: 'Completed',
      isMock: true
    }
  ].sort((a, b) => b.date.getTime() - a.date.getTime());
}

export const DEFAULT_ACCOUNTS = [
  {
    type: 'Checking',
    accountNumber: '1234567890',
    balance: 2500.0,
    available: 2500.0,
    routingNumber: '021000021'
  },
  {
    type: 'Savings',
    accountNumber: '0987654321',
    balance: 10000.0,
    available: 10000.0,
    routingNumber: '021000021'
  }
];

const INVESTMENT_RETURN_DESCRIPTIONS = [
  'Monthly Investment Return',
  'Portfolio Dividend Credit',
  'Equity Fund Distribution',
  'Bond Coupon Payment',
  'Reinvested Dividend - Oakvale Growth Fund',
  'Investment Yield Credit',
  'Market Appreciation Credit',
];

const QUARTERLY_RETURN_DESCRIPTIONS = [
  'Quarterly Portfolio Yield',
  'Quarterly Dividend Distribution',
  'QTR Investment Performance Credit',
];

const roundCurrency = (value) => Number(Math.max(0, value).toFixed(2));

/** Generate monthly investment return credits scaled to the account balance. */
export function generateInvestmentReturnTransactions(availableBalance, months = 12) {
  const balance = Math.max(0, Number(availableBalance) || 0);
  if (balance <= 0) return [];

  const annualRate = 0.072;
  const monthlyRate = annualRate / 12;
  const transactions = [];
  const now = new Date();

  for (let i = 0; i < months; i += 1) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1, 9, 15, 0, 0);
    const monthIndex = monthDate.getMonth() + 1;
    const isQuarterEnd = monthIndex % 3 === 0;

    const variation = 0.88 + ((i * 7 + monthIndex) % 13) * 0.01;
    const monthlyReturn = roundCurrency(balance * monthlyRate * variation);

    if (monthlyReturn > 0) {
      const descriptions = isQuarterEnd ? QUARTERLY_RETURN_DESCRIPTIONS : INVESTMENT_RETURN_DESCRIPTIONS;
      transactions.push({
        type: 'credit',
        amount: monthlyReturn,
        description: descriptions[i % descriptions.length],
        date: monthDate,
        category: 'Investment Returns',
        status: 'Completed',
      });
    }

    if (isQuarterEnd && balance >= 5000) {
      const fee = roundCurrency(balance * 0.0008);
      if (fee > 0) {
        const feeDate = new Date(monthDate);
        feeDate.setDate(5);
        feeDate.setHours(11, 0, 0, 0);
        transactions.push({
          type: 'debit',
          amount: fee,
          description: 'Portfolio Management Fee',
          date: feeDate,
          category: 'Investment Returns',
          status: 'Completed',
        });
      }
    }
  }

  return transactions.sort((a, b) => b.date.getTime() - a.date.getTime());
}